import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { pdfAPI } from '../services/api';
import { authService } from '../services/auth';
import { PDF } from '../types';
import { APIError } from '../types';
import UserManagement from './UserManagement';
import ChatMonitoring from './ChatMonitoring';
import StaffManagement from '../pages/StaffManagement';
import AnalyticsDashboard from '../pages/AnalyticsDashboard';

type AdminSection = 'dashboard' | 'documents' | 'students' | 'staff' | 'chats';

const SECTION_META: Record<AdminSection, { title: string; caption: string; blurb: string }> = {
  dashboard: {
    title: 'Dashboard',
    caption: 'BM Law College Management System',
    blurb: 'Usage, documents, and engagement at a glance for the AI assistant.',
  },
  documents: {
    title: 'Documents',
    caption: 'BM Law College Document Library',
    blurb: 'Upload and manage legal documents, case laws, and study materials for the AI assistant.',
  },
  students: {
    title: 'Students',
    caption: 'BM Law College Management System',
    blurb: 'Create, review, and manage student accounts and access.',
  },
  staff: {
    title: 'Team',
    caption: 'BM Law College Management System',
    blurb: 'Create and manage faculty and super admin accounts.',
  },
  chats: {
    title: 'Chat Monitoring',
    caption: 'BM Law College Management System',
    blurb: 'Review student conversations with the AI legal assistant.',
  },
};

const resolveSection = (pathname: string): AdminSection => {
  if (pathname.startsWith('/admin/documents')) return 'documents';
  if (pathname.startsWith('/admin/students')) return 'students';
  if (pathname.startsWith('/admin/staff')) return 'staff';
  if (pathname.startsWith('/admin/chats')) return 'chats';
  return 'dashboard';
};

const AdminPanel: React.FC = () => {
  const location = useLocation();
  const section = useMemo<AdminSection>(() => resolveSection(location.pathname), [location.pathname]);
  const meta = SECTION_META[section];

  /* Documents is reachable by faculty too — superadmins may delete anything,
     faculty only the PDFs they uploaded themselves (mirrors the API rule). */
  const isSuperAdmin = authService.isSuperAdmin();
  const currentUserId = authService.getUser()?.id;
  const canDeletePDF = useCallback(
    (pdf: PDF) =>
      isSuperAdmin || (currentUserId != null && pdf.uploaded_by_id === currentUserId),
    [isSuperAdmin, currentUserId]
  );

  const [pdfs, setPdfs] = useState<PDF[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const fetchPDFs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await pdfAPI.getAll();
      setPdfs(response.pdfs);
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to fetch PDFs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section === 'documents') {
      fetchPDFs();
    }
  }, [section, fetchPDFs]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      await pdfAPI.upload(file);
      setSuccess('PDF uploaded successfully');
      fetchPDFs();
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to upload PDF');
    } finally {
      setUploading(false);
      // Always clear the input — otherwise re-picking the SAME file after a
      // failed upload fires no change event and the retry silently does nothing
      e.target.value = '';
    }
  };

  const handleDeletePDF = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this PDF?')) return;

    try {
      await pdfAPI.delete(id);
      setSuccess('PDF deleted successfully');
      fetchPDFs();
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to delete PDF');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf') {
      const input = document.createElement('input');
      input.type = 'file';
      input.files = e.dataTransfer.files;
      handleFileUpload({ target: input } as any);
    } else {
      setError('Please drop a PDF file');
    }
  };

  return (
    <div className="px-3 sm:px-4 py-4 sm:py-6 animate-fade-in">
      {/* Page Hero */}
      <div className="page-hero mb-5 sm:mb-6">
        <div className="relative z-10 flex items-center gap-3 sm:gap-4">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white ring-2 ring-bmlc-gold-400 shadow-gold-glow overflow-hidden flex items-center justify-center flex-shrink-0">
            <img
              src="https://edifywebsite.blob.core.windows.net/edify-files/IMG_9812.JPG"
              alt="BM Law College Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-white leading-tight">{meta.title}</h1>
            <p className="text-xs sm:text-sm text-bmlc-gold-300 font-semibold tracking-wide">{meta.caption}</p>
            <p className="text-[11px] sm:text-xs text-bmlc-navy-100 mt-1">
              {meta.blurb}
            </p>
          </div>
        </div>
      </div>

      {/* Section content — driven by the route, navigation lives in the drawer */}
      {section === 'dashboard' ? (
        <AnalyticsDashboard />
      ) : section === 'students' ? (
        <UserManagement />
      ) : section === 'staff' ? (
        <StaffManagement />
      ) : section === 'chats' ? (
        <ChatMonitoring />
      ) : (
        <>
          {/* Original PDF Management Content */}
          <div className="mb-4 sm:mb-6">
            <p className="text-gray-600 text-xs sm:text-sm">
              {isSuperAdmin
                ? 'Every document in the shared library. You can remove any upload.'
                : 'Documents you add join the shared library. You can remove the ones you uploaded.'}
            </p>
          </div>

      {/* Upload Section */}
      <div className="card-premium p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="section-title">Upload Legal Documents</h2>

        {error && (
          <div className="toast-error mb-3 sm:mb-4">
            <span className="text-sm sm:text-base">⚠️</span>
            <p className="text-xs sm:text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="toast-success mb-3 sm:mb-4">
            <span className="text-sm sm:text-base">✅</span>
            <p className="text-xs sm:text-sm">{success}</p>
          </div>
        )}

        <div
          className={`border-2 border-dashed rounded-2xl p-4 sm:p-6 md:p-8 text-center transition-all duration-300 ease-out-soft ${
            dragActive
              ? 'border-bmlc-gold-500 bg-bmlc-gold-50 shadow-gold-glow'
              : 'border-bmlc-navy-200 bg-bmlc-navy-50 hover:border-bmlc-gold-400 hover:bg-bmlc-gold-50 hover:shadow-gold-glow'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center space-y-3 sm:space-y-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full ring-2 ring-bmlc-gold-300 shadow-soft flex items-center justify-center">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-bmlc-navy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div>
              <p className="text-bmlc-navy-900 text-sm sm:text-base font-semibold mb-1">
                Drop PDF files here or click to browse
              </p>
              <p className="text-gray-500 text-xs sm:text-sm">
                Upload legal documents, case laws, and study materials
              </p>
            </div>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              disabled={uploading}
            />
            <label
              htmlFor="file-upload"
              className={`btn-primary px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg text-white font-semibold cursor-pointer inline-flex items-center space-x-2 text-xs sm:text-sm ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <span>Select PDF File</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* PDF List */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
          <h2 className="section-title mb-0">
            Uploaded Documents ({pdfs.length})
          </h2>
          <button
            onClick={fetchPDFs}
            className="btn-ghost p-1.5 sm:p-2 rounded-lg flex-shrink-0"
            title="Refresh"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="py-4 sm:py-6">
            <div className="space-y-3">
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-2/3"></div>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 text-center">Loading documents...</p>
          </div>
        ) : pdfs.length === 0 ? (
          <div className="text-center py-10 sm:py-14">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bmlc-navy-50 ring-1 ring-bmlc-navy-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-soft-sm">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-bmlc-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-bmlc-navy-900 font-serif font-semibold text-sm sm:text-base mb-1">No documents uploaded yet</p>
            <p className="text-gray-400 text-xs sm:text-sm">Upload your first legal document to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="table-premium min-w-full">
              <thead>
                <tr>
                  <th className="text-left">
                    Document Name
                  </th>
                  <th className="text-left">
                    Size
                  </th>
                  <th className="text-left hidden sm:table-cell">
                    Uploaded By
                  </th>
                  <th className="text-left hidden md:table-cell">
                    Date
                  </th>
                  <th className="text-left">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pdfs.map((pdf) => (
                  <tr key={pdf.id}>
                    <td className="whitespace-nowrap">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-bmlc-maroon-50 ring-1 ring-bmlc-maroon-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 sm:w-6 sm:h-6 text-bmlc-maroon-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-bmlc-navy-900 truncate max-w-[120px] sm:max-w-[200px]">{pdf.original_name}</div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600">
                      {formatFileSize(pdf.content_length)}
                    </td>
                    <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden sm:table-cell">
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-bmlc-gold-50 ring-1 ring-bmlc-gold-300 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-semibold text-bmlc-gold-700 flex-shrink-0">
                          {pdf.uploaded_by?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <span className="truncate max-w-[100px] sm:max-w-[150px]">{pdf.uploaded_by || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden md:table-cell">
                      {formatDate(pdf.created_at)}
                    </td>
                    <td className="whitespace-nowrap text-xs sm:text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => window.open(pdfAPI.view(pdf.id), '_blank')}
                          className="btn-ghost inline-flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold"
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span className="hidden sm:inline">View</span>
                        </button>
                        {canDeletePDF(pdf) && (
                          <button
                            onClick={() => handleDeletePDF(pdf.id)}
                            className="btn-danger-soft inline-flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default AdminPanel;
